import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner, faEye, faEyeSlash, faGraduationCap, faCog } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import ToggleButton from '@/components/ui/ToggleButton';
import {
  type User, type Course, type CourseID, type PermissionGroup, type Subscription,
  type FeatureConfigKey
} from '@/models';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { APIRoutes } from '@/services/api.routes';
import { PAGINATION } from '@/config';
import { showToast } from '@/utils/toasts';
import { formatCurrencyCentValue, toCurrencyCentValue, validateSCNUID, validateSCNToken, validatePhoneE164, normalizePhoneNumber } from '@/utils/helpers';

interface AdminEditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onUserUpdated: (updatedUser: User) => void;
}

export default function AdminEditUserModal({ isOpen, onClose, user, onUserUpdated }: AdminEditUserModalProps) {
  const { getPeriodLabel } = useSubscriptionProducts();
  const [email, setEmail] = useState(user.email);
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [phone, setPhone] = useState(user.phone);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [permissionGroup, setPermissionGroup] = useState(user.permissionGroup);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [scnUID, setScnUID] = useState(user.scnUID || '');
  const [scnToken, setScnToken] = useState(user.scnToken || '');
  const [isLoading, setIsLoading] = useState(false);
  const [scnErrors, setScnErrors] = useState<{ scnUID?: string; scnToken?: string }>({});

  // Manual feature config state (editable by admin)
  const [manualFeatureConfig, setManualFeatureConfig] = useState(user.manualFeatureConfig);
  const [courses, setCourses] = useState<Course[]>([]);

  // Subscriptions state (read-only)
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(null);

  // Fetch courses and subscriptions on mount
  useEffect(() => {
    fetchCourses();
    if (isOpen) {
      fetchSubscriptions();
    }
  }, [isOpen]);

  // Reset form when user changes
  useEffect(() => {
    setEmail(user.email);
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setPhone(user.phone);
    setPhoneTouched(false);
    setPermissionGroup(user.permissionGroup);
    setNewPassword('');
    setShowPassword(false);
    setScnUID(user.scnUID || '');
    setScnToken(user.scnToken || '');
    setScnErrors({});
    setManualFeatureConfig(user.manualFeatureConfig);
  }, [user]);

  const fetchCourses = async () => {
    try {
      const response = await APIRoutes.listCourses({ limit: PAGINATION.FETCH_ALL_LIMIT });
      setCourses(response.courses);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSubscriptions = async () => {
    setLoadingSubscriptions(true);
    setSubscriptionsError(null);
    try {
      const response = await APIRoutes.getUserSubscriptions(user.id);
      setSubscriptions(response.subscriptions);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      const rawMessage = error instanceof Error ? error.message : String(error);
      const statusMatch = rawMessage.match(/^HTTP\s+(\d{3}):/);
      const statusCode = statusMatch !== null ? Number(statusMatch[1]) : null;
      if (statusCode === 502 || statusCode === 503) {
        setSubscriptionsError('Subscriptions konnten fuer diesen Benutzer aktuell nicht geladen werden (Serverfehler 5xx).');
      } else {
        setSubscriptionsError('Subscriptions konnten nicht geladen werden.');
      }
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !firstName.trim() || !lastName.trim()) {
      showToast('E-Mail, Vorname und Nachname müssen ausgefüllt werden', 'error');
      return;
    }

    if (newPassword && newPassword.length < 8) {
      showToast('Das neue Passwort muss mindestens 8 Zeichen lang sein', 'error');
      return;
    }

    // Validate SCN fields if provided
    const newScnErrors: { scnUID?: string; scnToken?: string } = {};
    if (scnUID.trim() !== '') {
      const scnUIDValidation = validateSCNUID(scnUID);
      if (!scnUIDValidation.valid) {
        newScnErrors.scnUID = scnUIDValidation.error;
      }
    }
    if (scnToken.trim() !== '') {
      const scnTokenValidation = validateSCNToken(scnToken);
      if (!scnTokenValidation.valid) {
        newScnErrors.scnToken = scnTokenValidation.error;
      }
    }
    if (Object.keys(newScnErrors).length > 0) {
      setScnErrors(newScnErrors);
      showToast('Bitte korrigieren Sie die SCN-Konfigurationsfehler', 'error');
      return;
    }

    if (phone.trim() !== '') {
      const phoneValidation = validatePhoneE164(phone);
      if (!phoneValidation.valid) {
        showToast(phoneValidation.error ?? 'Ungültige Telefonnummer', 'error');
        return;
      }
    }

    setIsLoading(true);
    try {
      // Prepare update request
      const updateRequest = {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: normalizePhoneNumber(phone),
        permissionGroup,
        password: newPassword.trim() || undefined,
        manualFeatureConfig: manualFeatureConfig,
        scnUID: scnUID.trim() || undefined,
        scnToken: scnToken.trim() || undefined
      };

      const updatedUser = await APIRoutes.updateUser(user.id, updateRequest);

      if (newPassword.trim()) {
        showToast('Benutzer und Passwort erfolgreich aktualisiert', 'success');
      } else {
        showToast('Benutzer erfolgreich aktualisiert', 'success');
      }

      onUserUpdated(updatedUser);
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
      showToast('Fehler beim Aktualisieren des Benutzers', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (field: FeatureConfigKey) => {
    setManualFeatureConfig(prev => {
      const currentValue = prev[field];
      // Ensure we're working with boolean values
      const newValue = typeof currentValue === 'boolean' ? !currentValue : false;
      return {
        ...prev,
        [field]: newValue
      };
    });
  };

  const handleLimitChange = (field: 'coreRecommendationStrikeLimit' | 'earningRecommendationStrikeLimit' | 'swingTradeRecommendationPriceLimit' | 'longTermRecommendationPriceLimit', value: string) => {
    const numValue = parseFloat(value);
    setManualFeatureConfig(prev => ({
      ...prev,
      [field]: value === '' ? null : toCurrencyCentValue(numValue)
    }));
  };

  const handleCourseToggle = (courseId: CourseID) => {
    setManualFeatureConfig(prev => ({
      ...prev,
      courses: {
        ...prev.courses,
        [courseId]: !prev.courses[courseId]
      }
    }));
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form to original values
      setEmail(user.email);
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setPhone(user.phone);
      setPhoneTouched(false);
      setPermissionGroup(user.permissionGroup);
      setNewPassword('');
      setShowPassword(false);
      setScnUID(user.scnUID || '');
      setScnToken(user.scnToken || '');
      setScnErrors({});
      setManualFeatureConfig(user.manualFeatureConfig);
      onClose();
    }
  };

  const hasChanges =
    email.trim() !== user.email ||
    firstName.trim() !== user.firstName ||
    lastName.trim() !== user.lastName ||
    phone.trim() !== user.phone ||
    permissionGroup !== user.permissionGroup ||
    newPassword.trim() !== '' ||
    scnUID.trim() !== (user.scnUID || '') ||
    scnToken.trim() !== (user.scnToken || '') ||
    JSON.stringify(manualFeatureConfig) !== JSON.stringify(user.manualFeatureConfig);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Benutzer bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-blue-600" />}
      maxWidth="3xl"
      closeDisabled={isLoading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              E-Mail
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Telefon <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => setPhoneTouched(true)}
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white disabled:opacity-50 ${
                phoneTouched && phone.trim() !== '' && !validatePhoneE164(phone).valid
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="+491701234567"
            />
            {phoneTouched && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Mit Ländervorwahl, z. B. +491701234567
              </p>
            )}
            {phoneTouched && phone.trim() !== '' && !validatePhoneE164(phone).valid && (
              <p className="mt-1 text-xs text-red-500">
                Telefonnummer muss mit Ländervorwahl beginnen (z. B. +491701234567)
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Vorname
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                required
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nachname
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="permissionGroup" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Berechtigungsgruppe
            </label>
            <select
              id="permissionGroup"
              value={permissionGroup}
              onChange={(e) => setPermissionGroup(e.target.value as PermissionGroup)}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              required
            >
              <option value="USER">Benutzer</option>
              <option value="EMPLOYEE">Mitarbeiter</option>
              <option value="ADMIN">Administrator</option>
            </select>
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Neues Passwort
              <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">(optional)</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                placeholder="Leer lassen, um Passwort nicht zu ändern"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                disabled={isLoading}
              >
                <FontAwesomeIcon
                  icon={showPassword ? faEyeSlash : faEye}
                  className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Mindestens 8 Zeichen, falls ein neues Passwort gesetzt werden soll
            </p>
          </div>

          {/* SCN Configuration Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              SCN-Konfiguration (Simple Cloud Notifications)
            </h3>

            <div className="space-y-4">
              {/* SCN UID */}
              <div>
                <label htmlFor="scnUID" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SCN UID <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="scnUID"
                  value={scnUID}
                  onChange={(e) => {
                    setScnUID(e.target.value);
                    setScnErrors({ ...scnErrors, scnUID: undefined });
                  }}
                  disabled={isLoading}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 font-mono text-sm ${
                    scnErrors.scnUID
                      ? 'border-red-500 dark:border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="USR123456..."
                />
                {scnErrors.scnUID && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{scnErrors.scnUID}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Die SCN User ID muss mit "USR" beginnen
                </p>
              </div>

              {/* SCN Token */}
              <div>
                <label htmlFor="scnToken" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SCN Token <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="scnToken"
                  value={scnToken}
                  onChange={(e) => {
                    setScnToken(e.target.value);
                    setScnErrors({ ...scnErrors, scnToken: undefined });
                  }}
                  disabled={isLoading}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 font-mono text-sm ${
                    scnErrors.scnToken
                      ? 'border-red-500 dark:border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="64 alphanumerische Zeichen..."
                />
                {scnErrors.scnToken && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{scnErrors.scnToken}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Der Token muss genau 64 alphanumerische Zeichen lang sein
                </p>
              </div>
            </div>
          </div>

          {/* Manual Feature Configuration Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center mb-4">
              <FontAwesomeIcon icon={faCog} className="w-4 h-4 mr-2 text-gray-700 dark:text-gray-300" />
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Manuelle Feature-Konfiguration</h3>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Diese Features werden manuell gewährt, zusätzlich zu den Features aus aktiven Subscriptions.
            </p>

            <div className="space-y-3 max-h-96 overflow-y-auto">
                {/* Core Recommendations */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="mr-3">
                      <ToggleButton
                        enabled={manualFeatureConfig.coreRecommendations}
                        onClick={() => handleToggle('coreRecommendations')}
                        disabled={isLoading}
                        size="md"
                      />
                    </div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Core-Tradingideen
                    </label>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={manualFeatureConfig.coreRecommendationStrikeLimit !== null ? formatCurrencyCentValue(manualFeatureConfig.coreRecommendationStrikeLimit, 'USD') : ''}
                    onChange={(e) => handleLimitChange('coreRecommendationStrikeLimit', e.target.value)}
                    disabled={isLoading || !manualFeatureConfig.coreRecommendations}
                    placeholder="Kein Limit (USD)"
                    className="w-32 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                  />
                </div>

                {/* Earning Recommendations */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleToggle('earningRecommendations')}
                      disabled={isLoading}
                      className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        manualFeatureConfig.earningRecommendations
                          ? 'bg-green-600 dark:bg-green-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          manualFeatureConfig.earningRecommendations ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Earnings-Tradingideen
                    </label>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={manualFeatureConfig.earningRecommendationStrikeLimit !== null ? formatCurrencyCentValue(manualFeatureConfig.earningRecommendationStrikeLimit, 'USD') : ''}
                    onChange={(e) => handleLimitChange('earningRecommendationStrikeLimit', e.target.value)}
                    disabled={isLoading || !manualFeatureConfig.earningRecommendations}
                    placeholder="Kein Limit (USD)"
                    className="w-32 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                  />
                </div>

                {/* Swing Trade Recommendations */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleToggle('swingTradeRecommendations')}
                      disabled={isLoading}
                      className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        manualFeatureConfig.swingTradeRecommendations
                          ? 'bg-green-600 dark:bg-green-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          manualFeatureConfig.swingTradeRecommendations ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Swing-Tradingideen
                    </label>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={manualFeatureConfig.swingTradeRecommendationPriceLimit !== null ? formatCurrencyCentValue(manualFeatureConfig.swingTradeRecommendationPriceLimit, 'USD') : ''}
                    onChange={(e) => handleLimitChange('swingTradeRecommendationPriceLimit', e.target.value)}
                    disabled={isLoading || !manualFeatureConfig.swingTradeRecommendations}
                    placeholder="Kein Limit (USD)"
                    className="w-32 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                  />
                </div>

                {/* Long Term Recommendations */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleToggle('longTermRecommendations')}
                      disabled={isLoading}
                      className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        manualFeatureConfig.longTermRecommendations
                          ? 'bg-green-600 dark:bg-green-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          manualFeatureConfig.longTermRecommendations ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Langzeit-Tradingideen
                    </label>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={manualFeatureConfig.longTermRecommendationPriceLimit !== null ? formatCurrencyCentValue(manualFeatureConfig.longTermRecommendationPriceLimit, 'USD') : ''}
                    onChange={(e) => handleLimitChange('longTermRecommendationPriceLimit', e.target.value)}
                    disabled={isLoading || !manualFeatureConfig.longTermRecommendations}
                    placeholder="Kein Limit (USD)"
                    className="w-32 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                  />
                </div>

                {/* Boost Recommendations */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleToggle('boostRecommendations')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.boostRecommendations
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.boostRecommendations ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Boost-Tradingideen
                  </label>
                </div>

                {/* Community Member */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleToggle('communityMember')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.communityMember
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.communityMember ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Community-Mitglied
                  </label>
                </div>

                {/* Chatbot */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleToggle('chatbot')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.chatbot
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.chatbot ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Chatbot</label>
                </div>

                {/* Video Chatbot */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleToggle('videoChatbot')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.videoChatbot
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.videoChatbot ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Video-Chatbot</label>
                </div>

                {/* Journal */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleToggle('journal')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.journal
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.journal ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Journal</label>
                </div>

                {/* Day Trading */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleToggle('dayTrading')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.dayTrading
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.dayTrading ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Day Trading</label>
                </div>

                {/* Options-Screener */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleToggle('optionScreener')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.optionScreener
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.optionScreener ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Options-Screener</label>
                </div>

                {/* Video-Finder */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleToggle('videoSearch')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.videoSearch
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.videoSearch ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Video-Finder</label>
                </div>

                {/* All Courses */}
                <div className="flex items-center border-t border-gray-200 dark:border-gray-700 pt-3">
                  <button
                    type="button"
                    onClick={() => handleToggle('allCourses')}
                    disabled={isLoading}
                    className={`mr-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      manualFeatureConfig.allCourses
                        ? 'bg-green-600 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        manualFeatureConfig.allCourses ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                    <FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4 mr-2" />
                    Alle Kurse
                  </label>
                </div>

                {/* Individual Courses */}
                {courses.length > 0 && (
                  <div className="ml-11 space-y-2">
                    {courses.map(course => (
                      <div key={course.id} className="flex items-center">
                        <button
                          type="button"
                          onClick={() => handleCourseToggle(course.id)}
                          disabled={isLoading || manualFeatureConfig.allCourses}
                          className={`mr-3 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                            (manualFeatureConfig.courses[course.id] ?? false)
                              ? 'bg-green-600 dark:bg-green-500'
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              (manualFeatureConfig.courses[course.id] ?? false) ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <label className={`text-sm ${manualFeatureConfig.allCourses ? 'text-gray-400 dark:text-gray-600' : 'text-gray-600 dark:text-gray-400'}`}>
                          {course.title}
                          {!course.published && (
                            <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400">
                              Entwurf
                            </span>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>

          {/* Active Subscriptions Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Aktive Subscriptions
            </h3>
            {loadingSubscriptions ? (
              <div className="flex items-center justify-center py-4">
                <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Lade Subscriptions...</span>
              </div>
            ) : subscriptionsError !== null ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                {subscriptionsError}
              </p>
            ) : subscriptions.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                Keine aktiven Subscriptions
              </p>
            ) : (
              <div className="space-y-2">
                {subscriptions.map(subscription => (
                  <div key={subscription.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {subscription.items.map(item => item.type).join(', ')}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Status: <span className={`font-semibold ${
                            subscription.status === 'ACTIVE' ? 'text-green-600 dark:text-green-400' :
                            subscription.status === 'PAST_DUE' ? 'text-yellow-600 dark:text-yellow-400' :
                            subscription.status === 'CANCELED' ? 'text-red-600 dark:text-red-400' :
                            'text-gray-600 dark:text-gray-400'
                          }`}>
                            {subscription.status}
                          </span>
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                        {formatCurrencyCentValue(subscription.price, 'USD')} / {getPeriodLabel(subscription.items[0].type)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Computed Feature Configuration Section (read-only) */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Berechnete Features (aus Subscriptions + Manuell)
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Dies ist die finale Kombination aus manuellen Features und aktiven Subscriptions (read-only).
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                <div className={user.featureConfig.coreRecommendations ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.coreRecommendations ? '✓' : '✗'} Core-Tradingideen
                </div>
                <div className={user.featureConfig.earningRecommendations ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.earningRecommendations ? '✓' : '✗'} Earnings-Tradingideen
                </div>
                <div className={user.featureConfig.swingTradeRecommendations ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.swingTradeRecommendations ? '✓' : '✗'} Swing-Tradingideen
                </div>
                <div className={user.featureConfig.longTermRecommendations ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.longTermRecommendations ? '✓' : '✗'} Langzeit-Tradingideen
                </div>
                <div className={user.featureConfig.boostRecommendations ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.boostRecommendations ? '✓' : '✗'} Boost-Tradingideen
                </div>
                <div className={user.featureConfig.communityMember ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.communityMember ? '✓' : '✗'} Community-Mitglied
                </div>
                <div className={user.featureConfig.chatbot ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.chatbot ? '✓' : '✗'} Chatbot
                </div>
                <div className={user.featureConfig.videoChatbot ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.videoChatbot ? '✓' : '✗'} Video-Chatbot
                </div>
                <div className={user.featureConfig.journal ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.journal ? '✓' : '✗'} Journal
                </div>
                <div className={user.featureConfig.dayTrading ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.dayTrading ? '✓' : '✗'} Day Trading
                </div>
                <div className={user.featureConfig.optionScreener ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.optionScreener ? '✓' : '✗'} Options-Screener
                </div>
                <div className={user.featureConfig.videoSearch ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.videoSearch ? '✓' : '✗'} Video-Finder
                </div>
                <div className={user.featureConfig.allCourses ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}>
                  {user.featureConfig.allCourses ? '✓' : '✗'} Alle Kurse
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={isLoading || !hasChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center"
          >
            {isLoading && <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />}
            Speichern
          </button>
        </div>
      </form>
    </Modal>
  );
}
